import { useEffect, useId, useState } from "react";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ACTION_WHERE, METRIC_WORDS, Metric, REVIEW_AFTER_DAYS, TEST_AFTER_DAYS_MAX, measurable,
  type ActionCost, type ActionEdit, type DecisionView, type GapWhere, type PastDecision, type ReviewAction,
  type ReviewBody, type ReviewResponse, type ReviewView, type TurnView,
} from "@joogoos/contracts";
import { api, ApiError } from "../api.js";
import { Icon } from "../ui/icons.js";
import { useNarrow } from "../ui/narrow.js";
import { setOnScreen } from "../reports/onScreen.js";
import { HEADLINE, KICKER, UNDER } from "../tabs/chrome.js";

/**
 * The marketing review ([35](../../../../docs/35-the-marketing-manager.md), ADR 0063): the screen under the Overview
 * where the owner reads what Joogoos counted in their week, what it could not see, and the one thing it proposes —
 * and says yes, changes it, or says no.
 *
 * **It is a page to decide on, not a report to read.** The recommendation leads in display type with what it costs,
 * the owner's own time, how it is counted and the day it is looked at again, and the three buttons are straight under
 * it: on a phone they are on the first screen, before a word of what happened. Everything that led to it — what
 * happened, what is uncertain, what Joogoos cannot see — is under the buttons for whoever wants the reasons.
 *
 * **Code wrote every number on it.** The sentences came from a model and survived `applyReview`, which drops any
 * figure the evidence does not hold; the price, the metric, the days and whether it is a test were set by code. The
 * screen draws what the route answers and works nothing out, so it can never say something the review did not.
 *
 * **A yes spends nothing.** Approving opens the work where the work is done — a draft in the Studio, the tab a gap is
 * fixed on — and the paid press, if there is one, is that tool's own button with its own price. The two paid presses
 * here are writing the review and asking about it, and both carry their price on the button (ADR 0012).
 *
 * The conversation sits beside the review on a desk and under it on a phone. A turn never decides (`conversation.ts`):
 * a change Joogoos proposes is shown with **Use this**, and only that press changes the recommendation.
 *
 * Coral is used once, on the one press the screen is for: start a review, approve one, or open the work a yes began.
 */
export type ReviewPlace = "Business" | "Studio" | "Customers" | "SEO";

export const REVIEW_KEY = ["review"] as const;
/** Shared by every Review my marketing button, so a review started on the Overview reads as being written here too. */
const START_KEY = ["review", "start"] as const;
/**
 * A review is written while the owner waits, in about a minute (the call's deadline is ninety seconds). Past this, a
 * review still marked as being written has gone wrong somewhere else: the screen says it is taking longer than it
 * should, and asking every few seconds will not fix it.
 */
const WRITING_FOR_MS = 3 * 60_000;
/**
 * When the server gives up on a review still marked as being written and every read says it failed — the same ten
 * minutes as `REVIEW_WRITING_STALE_MS` in `packages/db/src/review.ts`, written again here because the browser does not
 * import the database. If that one moves, this one moves with it.
 */
const WRITING_DEAD_MS = 10 * 60_000;
/** A minute past that, so the read that says it failed is one this tab makes, and not one it stopped just short of. */
const ASK_UNTIL_MS = WRITING_DEAD_MS + 60_000;
const DAY = 86_400_000;

const isWriting = (latest: ReviewView | null | undefined): latest is ReviewView => latest?.status === "writing";

/**
 * How often to ask again about a review being written: every three seconds while it is as recent as a review takes,
 * then every thirty until the server has had time to call it failed, then never. Before this the asking stopped at
 * three minutes for good, and a review the server failed at ten sat on screen as *taking longer* until a reload.
 */
const askEvery = (latest: ReviewView | null | undefined): number | false => {
  if (!isWriting(latest)) return false;
  const age = Date.now() - Date.parse(latest.createdAt);
  return age < WRITING_FOR_MS ? 3_000 : age < ASK_UNTIL_MS ? 30_000 : false;
};

/**
 * Whether a review being written has been at it longer than one takes — and it turns true at that moment, not at the
 * next read: a read that answers what it answered before draws nothing new, so without the one timer here the sentence
 * saying so would wait for something else on the screen to change.
 */
export function useLate(latest: ReviewView | null | undefined): boolean {
  const lateAt = isWriting(latest) ? Date.parse(latest.createdAt) + WRITING_FOR_MS : null;
  const [, redraw] = useState(0);
  useEffect(() => {
    if (lateAt === null) return;
    const wait = lateAt - Date.now();
    if (wait <= 0) return;
    const t = setTimeout(() => redraw((n) => n + 1), wait);
    return () => clearTimeout(t);
  }, [lateAt]);
  return lateAt !== null && Date.now() >= lateAt;
}

/**
 * The review, read once and shared by the Overview and this screen (docs/17 §performance 4). Asked again only while a
 * review is being written somewhere this tab did not start it — another tab, or a reload in the middle of one — and on
 * the slope `askEvery` says, and not at all otherwise, so a tab left on it goes quiet.
 */
export const useReview = () => useQuery({
  queryKey: REVIEW_KEY,
  queryFn: api.review,
  staleTime: 30_000,
  refetchInterval: (q: { state: { data?: ReviewResponse | undefined } }) => askEvery(q.state.data?.latest),
});

/**
 * Every write answers the whole review, so the screen redraws from the answer. A refusal can still have changed it —
 * a start the guards stopped is stored as failed, with its sentence — so the read is asked again after one rather than
 * leaving the screen on what it showed before the press.
 */
function useWrite<V>(fn: (v: V) => Promise<ReviewResponse>, opts: { also?: string[]; key?: readonly string[] } = {}) {
  const qc = useQueryClient();
  return useMutation({
    ...(opts.key ? { mutationKey: [...opts.key] } : {}),
    mutationFn: fn,
    onSuccess: (answer: ReviewResponse) => {
      qc.setQueryData(REVIEW_KEY, answer);
      for (const key of opts.also ?? []) void qc.invalidateQueries({ queryKey: [key] });
    },
    onError: () => { void qc.invalidateQueries({ queryKey: REVIEW_KEY }); },
  });
}

/** Writing a review: paid, so the balance in the header is asked for again once it is written. */
export const useStartReview = () => useWrite<void>(() => api.startReview(), { also: ["workspace"], key: START_KEY });
/** Whether a review is being written from any button in this tab. */
export const useStarting = () => useIsMutating({ mutationKey: [...START_KEY] }) > 0;

export function Review({ onBack, onOpen, onPlace }: {
  /** Back to the Overview, which is where the review lives (`/overview/review`). */
  onBack: () => void;
  /** A Studio project a yes started. */
  onOpen: (projectId: string) => void;
  /** A tab, and on Business its step: where a gap is fixed, or where a yes is finished. */
  onPlace: (tab: ReviewPlace, step?: string) => void;
}) {
  const q = useReview();
  const narrow = useNarrow();
  // Held here rather than in the button, because the button goes while the review is written and its error must not.
  const start = useStartReview();
  // Drawn as being written for as long as the row says so; asked about only while that is recent (`useReview`).
  const writing = useStarting() || q.data?.latest?.status === "writing";
  // A report filed from here says it was filed from the review, not from the Overview it sits under (ADR 0024 §2).
  useEffect(() => { setOnScreen({ step: "review" }); return () => setOnScreen({ step: null }); }, []);

  const r = q.data;
  const latest = r?.latest ?? null;
  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="px-4 md:px-6 py-2.5 border-b border-line flex flex-wrap items-center gap-x-3 gap-y-2">
        <button onClick={onBack} aria-label="Back to the Overview" className="inline-flex items-center gap-1 text-[13px] text-muted hover:text-ink">
          <Icon name="chevronLeft" size={14} /> Overview
        </button>
        <span className="text-line-2" aria-hidden="true">/</span>
        <h1 className="text-[15px] font-semibold">Marketing review</h1>
        <span className="hidden sm:inline text-[13px] text-muted">what to do next, and what came of the last one</span>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-5 md:py-7">
        {q.isPending ? <p className="text-sm text-faint">…</p>
          : !r ? <Failure error={q.error} />
          : !r.available ? <Unavailable />
          : writing ? <Writing latest={latest} />
          : !latest ? <Start r={r} start={start} />
          : latest.status === "failed" ? <Failed r={r} latest={latest} start={start} />
          : <Ready key={latest.id} r={r} review={latest} narrow={narrow} start={start} onOpen={onOpen} onPlace={onPlace} />}
      </div>
    </div>
  );
}

type Starter = ReturnType<typeof useStartReview>;

/* ── Before there is a review ────────────────────────────────────────────────────────────────────────────────── */

/** What a box with no model says: not an empty review, which would read as a week with nothing in it. */
function Unavailable() {
  return (
    <div className="max-w-[640px] rounded-[10px] border border-dashed border-line-2 bg-panel px-4 py-3">
      <p className="text-[13px] font-semibold">Marketing reviews are not switched on here yet.</p>
      <p className="text-[12.5px] text-muted mt-0.5 leading-relaxed">
        Nothing can be reviewed, and nothing is charged for it. Everything else in Joogoos works as it did.
      </p>
    </div>
  );
}

function Start({ r, start }: { r: ReviewResponse; start: Starter }) {
  return (
    <section className="max-w-[640px]">
      <h2 className={`${KICKER} text-muted`}>Marketing review</h2>
      <p className={HEADLINE}>What should you do next?</p>
      <p className={UNDER}>
        Joogoos reads your last seven days — who came to your website, the letters and the posts — and the searches
        over the four weeks Google Search Console last reported, and proposes one thing to do this week, with what it
        costs, how it is counted and when it is looked at again.
        {r.priceUsd ? ` It costs about ${money(r.priceUsd)} when you press it.` : ""} Nothing is done until you say yes.
      </p>
      {/* The price is in the sentence above, so it is not said again beside the button. */}
      <StartButton r={r} start={start} label="Review my marketing" priced={false} />
    </section>
  );
}

function Failed({ r, latest, start }: { r: ReviewResponse; latest: ReviewView; start: Starter }) {
  return (
    <section className="max-w-[640px]">
      <h2 className={`${KICKER} text-muted`}>Marketing review</h2>
      <p className={HEADLINE}>Your review could not be written.</p>
      <p className={UNDER}>{latest.error ?? "Nothing was changed. Try again."}</p>
      <StartButton r={r} start={start} label="Try again" shown={latest.error} />
    </section>
  );
}

/**
 * The paid press, with its price beside it, or the sentence saying why it cannot be pressed now — never a dead
 * button (ADR 0012). `shown` is a sentence already on screen, so a refusal saying the same is not said twice.
 */
function StartButton({ r, start, label, shown, priced = true }: {
  r: ReviewResponse; start: Starter; label: string; shown?: string | null; priced?: boolean;
}) {
  if (!r.canStart.ok) return <p className="mt-3.5 text-[13px] text-ink-2 leading-relaxed max-w-[52ch]">{r.canStart.why}</p>;
  return (
    <>
      <div className="mt-3.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <button
          onClick={() => start.mutate()}
          disabled={start.isPending}
          className="inline-flex items-center gap-1.5 bg-coral text-white rounded-lg px-3.5 py-2 text-[13px] font-semibold hover:bg-coral-2 disabled:opacity-50"
        >
          {label}
        </button>
        {priced && r.priceUsd ? <span className="text-[12px] text-muted tabular-nums">about {money(r.priceUsd)}</span> : null}
      </div>
      {start.error && start.error.message !== shown ? <Failure error={start.error} /> : null}
    </>
  );
}

function Writing({ latest }: { latest: ReviewView | null }) {
  const late = useLate(latest);
  return (
    <section className="max-w-[640px]" aria-live="polite">
      <h2 className={`${KICKER} text-muted`}>Marketing review</h2>
      <p className={HEADLINE}>Reading your week…</p>
      <p className={UNDER}>
        {late
          ? "This is taking longer than it should. Come back in a few minutes: one review is only ever charged once."
          : "Your website, your letters and your posts over the last seven days, and the searches over the four weeks Google last reported. It takes about a minute, and nothing is changed while it does."}
      </p>
    </section>
  );
}

/* ── A review to decide on ───────────────────────────────────────────────────────────────────────────────────── */

/**
 * The review, in two columns on a desk the way the Overview is — the review on the left, the conversation and what was
 * decided before on the right — and in one on a phone, in the same order, so the decision comes first either way.
 */
function Ready({ r, review, narrow, start, onOpen, onPlace }: {
  r: ReviewResponse; review: ReviewView; narrow: boolean; start: Starter;
  onOpen: (projectId: string) => void; onPlace: (tab: ReviewPlace, step?: string) => void;
}) {
  const body = review.body;
  const decision = review.decision;
  // The action that stands: the owner's own change or the other option they took, else the recommendation.
  const action = decision?.action ?? body?.actions[0];
  if (!body || !action) {
    return (
      <section className="max-w-[640px]">
        <p className="text-[13px] text-muted">This review has nothing in it to decide on.</p>
        <Again r={r} start={start} />
      </section>
    );
  }
  const proposed = decision?.state === "proposed";
  return (
    <div
      data-columns={narrow ? 1 : 2}
      className={`grid gap-y-10 ${narrow ? "grid-cols-1" : "grid-cols-[minmax(0,7fr)_minmax(0,4fr)] gap-x-12"}`}
    >
      <div className="min-w-0 flex flex-col gap-9">
        <Recommendation r={r} review={review} action={action} decision={decision} narrow={narrow} start={start} onOpen={onOpen} onPlace={onPlace} />
        <Findings review={review} body={body} onPlace={onPlace} />
        <Others review={review} body={body} action={action} proposed={proposed} mayDecide={r.mayDecide} />
      </div>
      <div className="min-w-0 flex flex-col gap-9">
        <Conversation r={r} review={review} action={action} proposed={proposed && r.mayDecide} />
        {/* The latest decision is the one at the top of the page already, so the list starts with the one before. */}
        <Past rows={r.past.filter((p) => p.id !== decision?.id)} who={who(r.mayDecide)} />
      </div>
    </div>
  );
}

function Recommendation({ r, review, action, decision, narrow, start, onOpen, onPlace }: {
  r: ReviewResponse; review: ReviewView; action: ReviewAction; decision: DecisionView | null; narrow: boolean; start: Starter;
  onOpen: (projectId: string) => void; onPlace: (tab: ReviewPlace, step?: string) => void;
}) {
  return (
    <section aria-label="What to do">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className={`${KICKER} text-muted`}>{review.evidence.window.label}</h2>
        {action.experiment ? (
          <span className="px-1.5 py-0.5 rounded border border-line-2 text-[10.5px] font-semibold text-ink-2">A small test</span>
        ) : null}
      </div>
      <p className={HEADLINE}>{action.title}</p>
      <p className={UNDER}>{action.why}</p>
      {action.detail ? <p className="text-[13px] text-ink-2 mt-2 leading-relaxed max-w-[60ch]">{action.detail}</p> : null}
      <Facts action={action} decision={decision} narrow={narrow} />
      {decision?.state === "proposed" ? (r.mayDecide ? <Decide review={review} action={action} /> : <OwnersCall />)
        : decision ? <Decided r={r} review={review} decision={decision} start={start} onOpen={onOpen} onPlace={onPlace} />
        : null}
    </section>
  );
}

/**
 * The quiet facts under the recommendation, each one set by code: what it costs, the owner's own time (an estimate,
 * so "about"), what it is counted by, the day it is looked at again, and what would be a good sign — a hope, never a
 * promise. A test says what makes it one, because "small test" alone is a word nobody asked for.
 *
 * Two columns of label and value on a desk; on a phone each fact runs on as one line of text across the whole width,
 * which is half the height, so the three buttons under them are still on the first screen.
 */
function Facts({ action, decision, narrow }: { action: ReviewAction; decision: DecisionView | null; narrow: boolean }) {
  // Counted from today while it waits for a yes. After one, the line under the decision says the day the yes set.
  const lookedAt = !decision || decision.state === "proposed" ? new Date(Date.now() + action.reviewAfterDays * DAY).toISOString() : null;
  const rows: Array<[string, React.ReactNode]> = [
    ["What it costs", costLine(action.cost)],
    ...(action.ownerMinutes > 0 ? [["Effort", `about ${action.ownerMinutes} minutes of your time`] as [string, string]] : []),
    ["Counted by", METRIC_WORDS[action.metric]],
    ...(lookedAt ? [["Looked at again", `on ${onDay(lookedAt)}`] as [string, string]] : []),
    ...(action.expected ? [["A good sign", action.expected] as [string, string]] : []),
    ...(action.experiment
      ? [["A small test", "This week was too thin to tell a pattern from luck, so it is tried for a few weeks and then looked at, not kept up for good."] as [string, string]]
      : []),
  ];
  if (narrow) {
    return (
      <dl className="mt-3.5 flex flex-col gap-1 text-[12.5px] leading-relaxed">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt className="inline text-muted">{label}: </dt>
            <dd className="inline text-ink-2">{value}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return (
    <dl className="mt-4 grid grid-cols-[minmax(0,8.5rem)_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-[12.5px] leading-relaxed max-w-[62ch]">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted">{label}</dt>
          <dd className="text-ink-2">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * What an editor sees where the buttons would be: the decision is the owner's (35 §the limits), and the API would
 * refuse anybody else anyway — so the screen says whose it is rather than offering three presses that each fail.
 */
function OwnersCall() {
  return (
    <p className="mt-5 text-[12.5px] text-muted leading-relaxed max-w-[60ch]">
      The owner of this business decides on its review. You can ask about it below.
    </p>
  );
}

/** Yes, a change, or no. Owner only: the screen draws it for the owner, and the API holds the same line. */
function Decide({ review, action }: { review: ReviewView; action: ReviewAction }) {
  const [open, setOpen] = useState<"change" | "no" | null>(null);
  // The yes carries the action this screen drew, so a change that landed since — another tab, a Use this in flight —
  // is answered "look again" rather than approved unseen (ADR 0063 point 3).
  const approve = useWrite<void>(() => api.approveReview(review.id, action), { also: ["projects"] });
  const toggle = (which: "change" | "no") => setOpen((o) => (o === which ? null : which));
  return (
    <div className="mt-5">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => approve.mutate()}
          disabled={approve.isPending}
          className="inline-flex items-center gap-1.5 bg-coral text-white rounded-lg px-4 py-2 text-[13px] font-semibold hover:bg-coral-2 disabled:opacity-50"
        >
          <Icon name="check" size={14} /> {approve.isPending ? "Saying yes…" : "Approve"}
        </button>
        <button
          onClick={() => toggle("change")} aria-expanded={open === "change"}
          className="px-3.5 py-2 rounded-lg border border-line-2 bg-panel text-[13px] font-medium hover:bg-page"
        >
          Change
        </button>
        <button
          onClick={() => toggle("no")} aria-expanded={open === "no"}
          className="px-3.5 py-2 rounded-lg text-[13px] font-medium text-muted hover:text-ink"
        >
          Not this
        </button>
      </div>
      <p className="mt-2 text-[11.5px] text-faint leading-relaxed max-w-[60ch]">
        Saying yes spends nothing and sends nothing: it opens the work where it is done
        {action.kind === "make_post" ? ", as a draft in the Studio that is only made when you press Generate there" : ""}.
      </p>
      <Failure error={approve.error} />
      {/* Keyed by the action, so a form left open while Do this instead or Use this swaps the standing one starts
          again from the new one — its words, its days, what it is counted by — and never saves the old one's over it. */}
      {open === "change" ? (
        <ChangeForm
          key={`${action.kind}|${action.title}|${action.detail}|${action.metric}|${action.reviewAfterDays}`}
          review={review} action={action} onDone={() => setOpen(null)}
        />
      ) : null}
      {open === "no" ? <SayNo review={review} onDone={() => setOpen(null)} /> : null}
    </div>
  );
}

/**
 * The owner's own words for it, when it is looked at again, and what it is counted by — never the kind, which is
 * which tool the yes opens. Only what can be counted for this business is offered: a reply count from a mailbox that
 * cannot read replies is a zero that means nothing (`measurable`). A test stays a test, held to four weeks.
 */
function ChangeForm({ review, action, onDone }: { review: ReviewView; action: ReviewAction; onDone: () => void }) {
  const id = useId();
  const [title, setTitle] = useState(action.title);
  const [detail, setDetail] = useState(action.detail);
  const [days, setDays] = useState(action.reviewAfterDays);
  const [metric, setMetric] = useState<Metric>(action.metric);
  const edit = useWrite<ActionEdit>((e) => api.editReview(review.id, e));
  const dayChoices = [...new Set([...REVIEW_AFTER_DAYS.filter((d) => !action.experiment || d <= TEST_AFTER_DAYS_MAX), action.reviewAfterDays])]
    .sort((a, b) => a - b);
  const metrics = action.kind === "fix_measurement"
    ? (["gap_closed"] as Metric[])
    : Metric.options.filter((m) => m !== "gap_closed" && (m === action.metric || measurable(m, review.evidence)));
  const save = () => edit.mutate({ title: title.trim(), detail: detail.trim(), reviewAfterDays: days, metric }, { onSuccess: onDone });
  const field = "w-full rounded-lg border border-line-2 bg-panel px-2.5 py-1.5 text-[13px] outline-none focus:border-ink";
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); save(); }}
      aria-label="Change the recommendation"
      className="mt-4 rounded-xl border border-line bg-panel p-3.5 flex flex-col gap-3 max-w-[560px]"
    >
      <span className="flex flex-col gap-1">
        <label htmlFor={`${id}-title`} className="text-[12px] font-semibold">What to do</label>
        <input id={`${id}-title`} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} required className={field} />
      </span>
      <span className="flex flex-col gap-1">
        <label htmlFor={`${id}-detail`} className="text-[12px] font-semibold">In more detail</label>
        <textarea id={`${id}-detail`} value={detail} onChange={(e) => setDetail(e.target.value)} maxLength={500} rows={3} className={`${field} resize-y`} />
      </span>
      <div className="flex flex-wrap gap-3">
        <span className="flex flex-col gap-1">
          <label htmlFor={`${id}-days`} className="text-[12px] font-semibold">Look at it again after</label>
          <select id={`${id}-days`} value={days} onChange={(e) => setDays(Number(e.target.value))} className={field}>
            {dayChoices.map((d) => <option key={d} value={d}>{daysWords(d)}</option>)}
          </select>
        </span>
        {metrics.length > 1 ? (
          <span className="flex flex-col gap-1 min-w-0">
            <label htmlFor={`${id}-metric`} className="text-[12px] font-semibold">Count it by</label>
            <select id={`${id}-metric`} value={metric} onChange={(e) => setMetric(e.target.value as Metric)} className={field}>
              {metrics.map((m) => <option key={m} value={m}>{METRIC_WORDS[m]}</option>)}
            </select>
          </span>
        ) : (
          <span className="flex flex-col gap-1 text-[12px]">
            <span className="font-semibold">Counted by</span>
            <span className="py-1.5 text-muted">{METRIC_WORDS[metrics[0] ?? action.metric]}</span>
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" disabled={edit.isPending || !title.trim()} className="px-3 py-1.5 rounded-lg bg-ink text-white text-[12.5px] font-semibold disabled:opacity-50">
          {edit.isPending ? "Saving…" : "Save the change"}
        </button>
        <button type="button" onClick={onDone} className="px-2.5 py-1.5 rounded-lg text-[12.5px] text-muted hover:text-ink">Cancel</button>
        <span className="text-[11.5px] text-faint">Free. Nothing is done until you approve it.</span>
      </div>
      <Failure error={edit.error} />
    </form>
  );
}

/** No, and why if the owner wants to say: the next review is told, so it does not propose the same again. */
function SayNo({ review, onDone }: { review: ReviewView; onDone: () => void }) {
  const id = useId();
  const [reason, setReason] = useState("");
  const no = useWrite<string>((why) => api.rejectReview(review.id, why || undefined));
  return (
    <div className="mt-4 rounded-xl border border-line bg-panel p-3.5 flex flex-col gap-2.5 max-w-[560px]">
      <label htmlFor={id} className="text-[12px] font-semibold">Why not, if you want to say</label>
      <textarea
        id={id} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={2}
        placeholder="We are shut that week, or we tried it last year"
        className="w-full rounded-lg border border-line-2 bg-panel px-2.5 py-1.5 text-[13px] outline-none focus:border-ink resize-y placeholder:text-faint"
      />
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => no.mutate(reason.trim())} disabled={no.isPending} className="px-3 py-1.5 rounded-lg bg-ink text-white text-[12.5px] font-semibold disabled:opacity-50">
          {no.isPending ? "Saying no…" : "Say no"}
        </button>
        <button onClick={onDone} className="px-2.5 py-1.5 rounded-lg text-[12.5px] text-muted hover:text-ink">Keep it</button>
        <span className="text-[11.5px] text-faint">The next review is told, so it does not propose the same again.</span>
      </div>
      <Failure error={no.error} />
    </div>
  );
}

/**
 * After the decision: what was said and when, where the work is, the press that opens it, and — once the day has come
 * — what came of it, in the sentence `judgeOutcome` wrote, which says "after" and never "because of".
 */
function Decided({ r, review, decision, start, onOpen, onPlace }: {
  r: ReviewResponse; review: ReviewView; decision: DecisionView; start: Starter;
  onOpen: (projectId: string) => void; onPlace: (tab: ReviewPlace, step?: string) => void;
}) {
  const done = useWrite<void>(() => api.doneReview(review.id));
  if (decision.state === "rejected") {
    return (
      <div className="mt-5 flex flex-col gap-1.5">
        <p className="text-[13.5px] font-semibold">{who(r.mayDecide)} said no{decision.decidedAt ? ` on ${onDay(decision.decidedAt)}` : ""}.</p>
        {decision.reason ? <p className="text-[12.5px] text-ink-2">“{decision.reason}”</p> : null}
        <p className="text-[12px] text-muted">Nothing was started. The next review is told, so it does not propose the same again.</p>
        <Again r={r} start={start} />
      </div>
    );
  }
  const ref = decision.executionRef;
  const open = ref?.kind === "project" ? () => onOpen(ref.id) : ref?.kind === "tab" ? () => onPlace(ref.tab, ref.step) : null;
  const waiting = decision.execution === "waiting_on_you";
  const primary = "inline-flex items-center gap-1.5 bg-coral text-white rounded-lg px-3.5 py-2 text-[13px] font-semibold hover:bg-coral-2 disabled:opacity-50";
  const quiet = "px-3.5 py-2 rounded-lg border border-line-2 bg-panel text-[13px] font-medium hover:bg-page disabled:opacity-50";
  return (
    <div className="mt-5 flex flex-col gap-1.5">
      <p className="text-[13.5px] font-semibold">
        {who(r.mayDecide)} said yes{decision.decidedAt ? ` on ${onDay(decision.decidedAt)}` : ""}.
        {decision.reviewOn ? ` It is looked at again on ${onDay(decision.reviewOn)}.` : ""}
      </p>
      <p className="text-[12.5px] text-muted leading-relaxed max-w-[60ch]">{executionWords(decision, r.mayDecide)}</p>
      {open || (waiting && r.mayDecide) ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {open ? <button onClick={open} className={primary}>{ACTION_WHERE[decision.action.kind].press} <span aria-hidden="true">→</span></button> : null}
          {waiting && r.mayDecide ? (
            <button onClick={() => done.mutate()} disabled={done.isPending} className={open ? quiet : primary}>
              {done.isPending ? "Marking it…" : "Mark it done"}
            </button>
          ) : null}
        </div>
      ) : null}
      <Failure error={done.error} />
      {decision.outcome ? (
        <div className="mt-4 border-t border-line pt-3 max-w-[60ch]">
          <h3 className={`${KICKER} text-muted`}>What came of it</h3>
          <p className="text-[13.5px] mt-1.5 leading-relaxed">{decision.outcome.says}</p>
          {decision.outcome.alongside.length ? (
            <p className="text-[11.5px] text-faint mt-1">Running at the same time: {decision.outcome.alongside.join("; ")}.</p>
          ) : null}
        </div>
      ) : null}
      <Again r={r} start={start} />
    </div>
  );
}

/**
 * Where a yes has got to, as the owner would say it. `done` is read from the project itself when one was started.
 * Somebody who may not decide is not told a fix is theirs, nor to press a Mark it done they are not shown.
 */
function executionWords(d: DecisionView, mayDecide: boolean): string {
  const studio = d.executionRef?.kind === "project";
  switch (d.execution) {
    case "not_started": return "Nothing has been started yet.";
    case "started": return studio ? "A draft is waiting in the Studio. Nothing is made, and nothing is charged, until you press Generate there." : "It has been started.";
    case "waiting_on_you":
      if (!mayDecide) return "This one is the owner's to do, and the owner marks it done.";
      return d.executionRef ? "This one is yours to do, where the button below takes you. Mark it done once it is." : "This one is yours to do. Mark it done once it is.";
    case "done": return d.action.kind === "keep_going" ? "Nothing to change: carry on as you are." : studio ? "It has been made." : "Done.";
    case "failed": return "It could not be started.";
    case "cancelled": return "Stopped.";
  }
}

/** The next review, once this one is decided. Quiet: the coral on this screen belongs to the work a yes opened. */
function Again({ r, start }: { r: ReviewResponse; start: Starter }) {
  if (!r.canStart.ok) return r.canStart.why ? <p className="mt-4 text-[12px] text-muted">{r.canStart.why}</p> : null;
  return (
    <div className="mt-5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <button
          onClick={() => start.mutate()} disabled={start.isPending}
          className="px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-[12.5px] font-medium hover:bg-page disabled:opacity-50"
        >
          Review my marketing
        </button>
        <span className="text-[11.5px] text-faint">a new review of the last seven days{r.priceUsd ? ` · about ${money(r.priceUsd)}` : ""}</span>
      </div>
      <Failure error={start.error} />
    </div>
  );
}

/* ── What it was written from ────────────────────────────────────────────────────────────────────────────────── */

/**
 * What happened, what is uncertain, and what Joogoos cannot see. The last is never left out: a review that is quiet
 * about a missing integration reads as a quiet week (35). Each gap that can be fixed from the app says where.
 */
function Findings({ review, body, onPlace }: { review: ReviewView; body: ReviewBody; onPlace: (tab: ReviewPlace, step?: string) => void }) {
  return (
    <div className="flex flex-col gap-7">
      <Block title="What happened">
        {body.happened.length ? <Lines lines={body.happened} /> : <p className="text-[13px] text-muted">Too little was counted this week to say anything for certain.</p>}
        {body.diagnosis ? <p className="mt-2.5 text-[13px] text-ink-2 leading-relaxed max-w-[62ch]">{body.diagnosis}</p> : null}
      </Block>
      {body.uncertain.length ? <Block title="What is uncertain"><Lines lines={body.uncertain} /></Block> : null}
      {review.gaps.length ? (
        <Block title="What Joogoos cannot see">
          <ul aria-label="What Joogoos cannot see" className="flex flex-col">
            {review.gaps.map((g) => (
              <li key={g.gap} className="border-t border-line py-2.5 first:border-t-0 first:pt-0">
                <p className="text-[13px] leading-relaxed max-w-[62ch]">{g.says}</p>
                {g.where ? <FixLink where={g.where} onPlace={onPlace} /> : null}
              </li>
            ))}
          </ul>
        </Block>
      ) : null}
    </div>
  );
}

const STEP_WORDS: Record<string, string> = { about: "About the business", site: "Website & analytics", customers: "Customers", channels: "Channels" };
const placeWords = (w: NonNullable<GapWhere>) => (w.tab === "Business" && w.step ? `Business → ${STEP_WORDS[w.step] ?? w.step}` : w.tab);

function FixLink({ where, onPlace }: { where: NonNullable<GapWhere>; onPlace: (tab: ReviewPlace, step?: string) => void }) {
  return (
    <button
      onClick={() => (where.step ? onPlace(where.tab, where.step) : onPlace(where.tab))}
      className="mt-1 text-[12px] font-semibold text-ink underline decoration-line-2 underline-offset-2 hover:decoration-ink"
    >
      Fix it on {placeWords(where)} <span aria-hidden="true">→</span>
    </button>
  );
}

/**
 * What else was worth doing, each with **Do this instead**: the whole of that option takes the recommendation's
 * place — its kind, its price, how it is counted — rather than its words being poured into the first one's shape.
 */
function Others({ review, body, action, proposed, mayDecide }: {
  review: ReviewView; body: ReviewBody; action: ReviewAction; proposed: boolean; mayDecide: boolean;
}) {
  const use = useWrite<0 | 1 | 2>((n) => api.editReview(review.id, { use: n }));
  const others = body.actions.slice(1, 3).map((a, i) => ({ a, n: (i === 0 ? 1 : 2) as 1 | 2 }));
  if (!others.length) return null;
  const standing = (a: ReviewAction) => a.kind === action.kind && a.title === action.title;
  const first = body.actions[0];
  // Another option was taken: the way back to what was recommended is one press, not a new review.
  const back = proposed && mayDecide && first && !standing(first);
  return (
    <Block title="Other options">
      <ul aria-label="Other options" className="flex flex-col">
        {others.map(({ a, n }) => (
          <li key={n} className="border-t border-line py-3 first:border-t-0 first:pt-0 flex flex-col gap-1">
            <span className="text-[13.5px] font-semibold">{a.title}</span>
            <span className="text-[12.5px] text-muted leading-relaxed max-w-[62ch]">{a.why}</span>
            <span className="text-[11.5px] text-faint">{shortCost(a.cost)} · counted by {METRIC_WORDS[a.metric]}{a.experiment ? " · a small test" : ""}</span>
            {!proposed || !mayDecide ? null : standing(a) ? (
              <span className="text-[11.5px] font-semibold text-ink-2">This is the one standing now.</span>
            ) : (
              <span>
                <button
                  onClick={() => use.mutate(n)} disabled={use.isPending}
                  aria-label={`Do this instead: ${a.title}`}
                  className="mt-1 px-2.5 py-1 rounded-lg border border-line-2 bg-panel text-[12px] font-medium hover:bg-page disabled:opacity-50"
                >
                  Do this instead
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {back ? (
        <button
          onClick={() => use.mutate(0)} disabled={use.isPending}
          className="mt-2 text-[12px] font-semibold text-ink underline decoration-line-2 underline-offset-2 hover:decoration-ink disabled:opacity-50"
        >
          Go back to the first recommendation: {first.title}
        </button>
      ) : null}
      <Failure error={use.error} />
    </Block>
  );
}

/* ── Talking about it ────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * Questions about the review, typed. Each one is a paid turn with its price on Send, answered from the evidence and
 * the review only; a change Joogoos proposes is drawn with **Use this**, and only while the decision is still to make.
 * There is a limit to them on one review, and the screen says how many are left rather than refusing the last.
 */
function Conversation({ r, review, action, proposed }: { r: ReviewResponse; review: ReviewView; action: ReviewAction; proposed: boolean }) {
  const id = useId();
  const [text, setText] = useState("");
  const ask = useWrite<string>((t) => api.askReview(review.id, t), { also: ["workspace"] });
  const use = useWrite<ActionEdit>((change) => api.editReview(review.id, change));
  const left = r.limits.turnsPerReview - review.turns.filter((t) => t.role === "owner").length;
  const send = () => {
    const said = text.trim();
    if (!said || ask.isPending) return;
    ask.mutate(said, { onSuccess: () => setText("") });
  };
  return (
    <section aria-label="Ask about it">
      <h2 className={`${KICKER} text-muted`}>Ask about it</h2>
      {review.turns.length ? (
        <ol className="mt-3 flex flex-col gap-2">
          {review.turns.map((t) => (
            <Turn key={t.id} turn={t} action={action} proposed={proposed} busy={use.isPending} onUse={(c) => use.mutate(c)} />
          ))}
        </ol>
      ) : (
        <p className="mt-1.5 text-[12.5px] text-muted leading-relaxed">
          Anything the review said, or what you would rather do. Joogoos answers from what it counted, and never changes
          anything itself.
        </p>
      )}
      {ask.isPending ? <p className="mt-2 text-[12.5px] text-faint" aria-live="polite">Joogoos is answering…</p> : null}
      {left > 0 ? (
        <div className="mt-3 rounded-xl border border-line-2 bg-panel p-2.5 flex flex-col gap-2">
          <label htmlFor={id} className="sr-only">Ask about this review, or say what you would do instead</label>
          <textarea
            id={id} value={text} onChange={(e) => setText(e.target.value)} maxLength={r.limits.said} rows={2}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask about this review, or say what you would do instead"
            className="resize-none bg-transparent text-[13px] outline-none placeholder:text-faint field-sizing-content max-h-40"
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-faint">{left === 1 ? "one question left on this review" : `${left} questions left on this review`}</span>
            <button
              onClick={send} disabled={!text.trim() || ask.isPending}
              className="px-3 py-1.5 rounded-lg bg-ink text-white text-[12px] font-semibold disabled:opacity-40"
            >
              Send{r.turnUsd ? ` · ${money(r.turnUsd)}` : ""}
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-3 text-[12.5px] text-muted leading-relaxed">
          That is as many questions as one review takes. Decide on it, or start the next one once you have.
        </p>
      )}
      <Failure error={ask.error} />
      <Failure error={use.error} />
    </section>
  );
}

/**
 * One turn: the owner's on the right in ink, Joogoos's on the left on white — the review sits on the page colour, not
 * on a panel as the Studio's chat does — and a proposed change under its own words.
 */
function Turn({ turn, action, proposed, busy, onUse }: {
  turn: TurnView; action: ReviewAction; proposed: boolean; busy: boolean; onUse: (change: ActionEdit) => void;
}) {
  const mine = turn.role === "owner";
  const change = turn.change;
  const inUse = !!change && change.title === action.title && change.detail === action.detail
    && change.reviewAfterDays === action.reviewAfterDays && change.metric === action.metric;
  return (
    <li className={`max-w-[92%] rounded-xl px-3 py-2 text-[13px] leading-relaxed whitespace-pre-line ${mine ? "ml-auto bg-ink text-white" : "bg-panel border border-line text-ink"}`}>
      <span className="sr-only">{mine ? "You: " : "Joogoos: "}</span>
      {turn.text}
      {change && !mine ? (
        <div className="mt-2 rounded-lg border border-line bg-page px-2.5 py-2 flex flex-col gap-1 whitespace-normal">
          <span className="text-[10px] font-bold uppercase tracking-[.08em] text-faint">Proposed instead</span>
          <span className="text-[12.5px] font-semibold">{change.title}</span>
          {change.detail ? <span className="text-[12px] text-ink-2">{change.detail}</span> : null}
          <span className="text-[11.5px] text-muted">looked at again after {daysWords(change.reviewAfterDays)} · counted by {METRIC_WORDS[change.metric]}</span>
          {!proposed ? null : inUse ? (
            <span className="text-[11.5px] font-semibold text-ink-2">In use</span>
          ) : (
            <span>
              <button
                onClick={() => onUse(change)} disabled={busy}
                className="mt-0.5 px-2.5 py-1 rounded-lg bg-ink text-white text-[11.5px] font-semibold disabled:opacity-50"
              >
                Use this
              </button>
            </span>
          )}
        </div>
      ) : null}
    </li>
  );
}

/** What was decided before, and what came of each: the history the next review is given, shown to the owner too. */
function Past({ rows, who }: { rows: PastDecision[]; who: string }) {
  if (!rows.length) return null;
  return (
    <section>
      <h2 className={`${KICKER} text-muted`}>Decided before</h2>
      <ul aria-label="Decided before" className="mt-2 flex flex-col">
        {rows.map((p) => (
          <li key={p.id} className="border-t border-line py-2.5">
            <span className="block text-[13px] font-medium">{p.title}</span>
            <span className="block text-[11.5px] text-faint mt-0.5">
              {p.state === "approved" ? `${who} said yes` : p.state === "rejected" ? `${who} said no` : "Not decided"}
              {p.decidedAt ? ` on ${onDay(p.decidedAt)}` : ""}
            </span>
            {p.outcome ? <span className="block text-[12px] text-muted leading-relaxed mt-1">{p.outcome.says}</span> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── the small parts ─────────────────────────────────────────────────────────────────────────────────────────── */

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title}>
      <h2 className={`${KICKER} text-muted`}>{title}</h2>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function Lines({ lines }: { lines: string[] }) {
  return (
    <ul className="flex flex-col gap-1.5 max-w-[62ch]">
      {lines.map((l) => (
        <li key={l} className="flex gap-2.5 text-[13.5px] leading-relaxed">
          <span className="mt-[9px] w-1 h-1 rounded-full bg-line-2 shrink-0" aria-hidden="true" />
          {l}
        </li>
      ))}
    </ul>
  );
}

/** A refusal or a failure, in the API's own words and with its reference — the number a user reads down the phone. */
function Failure({ error }: { error: Error | null }) {
  if (!error) return null;
  return (
    <p role="alert" className="mt-2 text-[12.5px] text-bad leading-relaxed max-w-[60ch]">
      {error.message}
      {error instanceof ApiError && error.ref ? <span className="opacity-70 font-mono"> · ref {error.ref}</span> : null}
    </p>
  );
}

/**
 * What it costs, as the owner reads it: `costOf`'s own sentence, which already names the price where there is one —
 * "About $0.17 for a post or $1.39 for a video" — and says where it is priced where there is not. A price it does not
 * name is put in front of it, so a figure is never left out.
 */
function costLine(c: ActionCost): string {
  return c.usd === null || Number(c.usd) === 0 || c.note.includes(`$${c.usd}`) ? c.note : `About $${c.usd}. ${c.note}`;
}

/** The same in three words, for the options under the recommendation. */
function shortCost(c: ActionCost): string {
  return c.usd === null ? "priced on its own button" : Number(c.usd) === 0 ? "nothing to pay" : `about $${c.usd}`;
}

/** Who decided, as the reader would say it: the owner reads "You", and anybody else reads whose decision it was. */
export const who = (mayDecide: boolean) => (mayDecide ? "You" : "The owner");

/** A price on a button. A question costs a fraction of a cent, and "$0.00" would read as free. */
export const money = (usd: string) => (Number(usd) < 0.005 ? "under 1¢" : `$${Number(usd).toFixed(2)}`);

/** "16 October": the day, the way the evidence names its own. */
export const onDay = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long" });

const DAYS_WORDS: Record<number, string> = { 7: "a week", 14: "two weeks", 21: "three weeks", 28: "four weeks", 42: "six weeks" };
const daysWords = (d: number) => DAYS_WORDS[d] ?? `${d} days`;
